import React, { useEffect, useMemo, useState } from "react";
import * as Actions from "../../store/action/claim.js";
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TiersSection from './tiersSection.js';
import ExpertisesSection from './expertisesSection.js';
import './sinistreSection.css'
import HistoriqueSection from "./historiqueSection.js";
import PoliceSection from './policeSection.js';
import { makeStyles } from "@material-ui/core/styles";
import InformationSinistre from './informationSinistre.js'
import { useDispatch, useSelector } from "react-redux";
import VehiculeSection from "./vehiculeSection.js";
import DocSection from "./docSection.js";
import ImagesTab from "./photoSection.js";
// import PriseEnChargeSection from "./PriseEnChargeSection.js";
// import ReglementHonorairesSection from "./ReglementHonorairesSection";
import { useParams } from 'react-router-dom';
import UnfraudedSection from "./unfraudedSection.js";
import { hasAuthorities } from "../../utils/authoritiesUtils.js";
import Investigations from "./Investigations.js";
import { Vision360Provider, useVision360Cache } from "../../context/Vision360Context";
import * as PolicyActions from "../../store/action/policy.js";

const useStyles = makeStyles((theme) => ({
  titer: { fontFamily: 'Poppins', fontSize: '13px', fontWeight: 600, lineHeight: '19.5px', textAlign: 'center', background: 'rgba(255, 255, 255, 0.35)', color: 'rgba(63, 66, 84, 0.64)' },
  item: { fontFamily: 'Poppins', fontSize: '12.81px', fontWeight: 500, lineHeight: '19.22px', textAlign: 'left', color: 'rgba(181, 181, 195, 1)', padding: '3px' },
  paper: {
    width: "100%",
    boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.06)'
  },
}));

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <div style={{ width: "100%", background: 'rgba(255, 255, 255, 1)' }}>
          {children}
        </div>
      )}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export default function SinistreSection() {
  const dispatch = useDispatch();
  const { idClaim } = useParams();

  const infoSinistre = useSelector((state) => {
    const { claimReducer } = state;
    return Array.isArray(claimReducer.claimData)
      ? claimReducer.claimData.find((claim) => claim._id === idClaim)
      : undefined;
  });

  // Charger uniquement ce sinistre : getAllClaims ramenait toute la base et stockait
  // l'enveloppe { data, page, limit, total } au lieu d'un tableau.
  useEffect(() => {
    if (!infoSinistre) {
      dispatch(Actions.filterClaimm({ _id: idClaim }, 1, 1));
    }
  }, [idClaim]);

  return (
    <Vision360Provider>
      <SinistreSectionContent infoSinistre={infoSinistre} />
    </Vision360Provider>
  );
}

const SinistreSectionContent = ({ infoSinistre }) => {
  const [value, setValue] = useState(0);
  const [showAdvanced] = useState(true);
  const { prefetchVisionData } = useVision360Cache();

  const handleChange = (_, newValue) => {
    setValue(newValue);
  };

  const hasInvestigationAuth = useMemo(() => hasAuthorities("VIEW_INVESTIGATION_SECTION"), []);
  const hasUnfraudedAuth = useMemo(() => hasAuthorities("VIEW_UNFRAUDED_SECTION"), []);
  const unfraudedTabIndex = useMemo(() => hasInvestigationAuth ? 8 : 7, [hasInvestigationAuth]);

  const [policyHolderUid, setPolicyHolderUid] = useState(null);

  // Fetch policy holder from the policy instead of using InsuredDriver
  useEffect(() => {
    const fetchPolicyHolder = async () => {
      if (!infoSinistre?.involvedPolicies || infoSinistre.involvedPolicies.length === 0) {
        setPolicyHolderUid(null);
        return;
      }
      
      try {
        const policyUid = infoSinistre.involvedPolicies[0]?.policyUid;
        if (policyUid) {
          const policyData = await PolicyActions.getPolicyDetails(policyUid);
          if (policyData?.policyHolder) {
            setPolicyHolderUid(policyData.policyHolder);
          } else {
            // Fallback to InsuredDriver if policy holder not found
            const insuredDriver = infoSinistre.involvedParties?.find((party) => party?.role === "InsuredDriver");
            setPolicyHolderUid(insuredDriver?.partyUid || null);
          }
        }
      } catch (error) {
        console.warn("[SinistreSection] Error fetching policy holder, falling back to InsuredDriver:", error);
        // Fallback to InsuredDriver on error
        const insuredDriver = infoSinistre.involvedParties?.find((party) => party?.role === "InsuredDriver");
        setPolicyHolderUid(insuredDriver?.partyUid || null);
      }
    };

    fetchPolicyHolder();
  }, [infoSinistre]);

  const insuredCarUids = useMemo(() => {
    if (!infoSinistre?.involvedCars) return [];
    // Fetch all INSURED_CAR from involvedCars
    return infoSinistre.involvedCars
      .filter((car) => car?.role === "INSURED_CAR")
      .map((car) => car?.goodUid)
      .filter(Boolean);
  }, [infoSinistre]);

  useEffect(() => {
    if (!infoSinistre || !policyHolderUid) return;
    prefetchVisionData({
      clientUid: policyHolderUid,
      carUids: insuredCarUids,
    });
  }, [infoSinistre?.claimNumber, policyHolderUid, insuredCarUids, prefetchVisionData]);

  if (!infoSinistre) {
    return null;
  }

  return (
    <div>
      <InformationSinistre showAdvanced={showAdvanced} informationSinistre={infoSinistre} />
      <Tabs value={value} sx={{ marginTop: "40px", variant: "fullWidth" }} onChange={handleChange}>
        <Tab sx={{ textTransform: 'none', height: 50, fontFamily: 'Poppins', fontSize: '13px', fontWeight: 600, lineHeight: '19.5px', textAlign: 'center', background: 'rgba(255, 255, 255, 0.35)', color: 'rgba(63, 66, 84, 0.64)', marginRight: '5px' }} {...a11yProps(0)} label="Tiers" />
        <Tab sx={{ textTransform: 'none', height: 50, fontFamily: 'Poppins', fontSize: '13px', fontWeight: 600, lineHeight: '19.5px', textAlign: 'center', background: 'rgba(255, 255, 255, 0.35)', color: 'rgba(63, 66, 84, 0.64)', marginRight: '5px' }} {...a11yProps(1)} label="Polices" />
        <Tab sx={{ textTransform: 'none', height: 50, fontFamily: 'Poppins', fontSize: '13px', fontWeight: 600, lineHeight: '19.5px', textAlign: 'center', background: 'rgba(255, 255, 255, 0.35)', color: 'rgba(63, 66, 84, 0.64)', marginRight: '5px' }} {...a11yProps(2)} label="Véhicules" />
        <Tab sx={{ textTransform: 'none', height: 50, fontFamily: 'Poppins', fontSize: '13px', fontWeight: 600, lineHeight: '19.5px', textAlign: 'center', background: 'rgba(255, 255, 255, 0.35)', color: 'rgba(63, 66, 84, 0.64)', marginRight: '5px' }} {...a11yProps(3)} label="Photos" />
        <Tab sx={{ textTransform: 'none', height: 50, fontFamily: 'Poppins', fontSize: '13px', fontWeight: 600, lineHeight: '19.5px', textAlign: 'center', background: 'rgba(255, 255, 255, 0.35)', color: 'rgba(63, 66, 84, 0.64)', marginRight: '5px' }} {...a11yProps(4)} label="Documents" />
        <Tab sx={{ textTransform: 'none', height: 50, fontFamily: 'Poppins', fontSize: '13px', fontWeight: 600, lineHeight: '19.5px', textAlign: 'center', background: 'rgba(255, 255, 255, 0.35)', color: 'rgba(63, 66, 84, 0.64)', marginRight: '5px' }} {...a11yProps(5)} label="Expertises" />
        {/* <Tab sx={{ textTransform: 'none', height: 50, fontFamily: 'Poppins', fontSize: '13px', fontWeight: 600, lineHeight: '19.5px', textAlign: 'center', background: 'rgba(255, 255, 255, 0.35)', color: 'rgba(63, 66, 84, 0.64)', marginRight: '5px' }} {...a11yProps(6)} label="Prise en charge" />
        <Tab sx={{ textTransform: 'none', height: 50, fontFamily: 'Poppins', fontSize: '13px', fontWeight: 600, lineHeight: '19.5px', textAlign: 'center', background: 'rgba(255, 255, 255, 0.35)', color: 'rgba(63, 66, 84, 0.64)', marginRight: '5px' }} {...a11yProps(7)} label="Réglement Honoraires" /> */}
        <Tab sx={{ textTransform: 'none', height: 50, fontFamily: 'Poppins', fontSize: '13px', fontWeight: 600, lineHeight: '19.5px', textAlign: 'center', background: 'rgba(255, 255, 255, 0.35)', color: 'rgba(63, 66, 84, 0.64)', marginRight: '5px' }} {...a11yProps(6)} label="Mouvements" />
        {hasInvestigationAuth && (
        <Tab sx={{ textTransform: 'none', height: 50, fontFamily: 'Poppins', fontSize: '13px', fontWeight: 600, lineHeight: '19.5px', textAlign: 'center', background: 'rgba(255, 255, 255, 0.35)', color: 'rgba(63, 66, 84, 0.64)', marginRight: '5px' }} {...a11yProps(7)} label="Investigation" />
        )}
        {hasUnfraudedAuth && (
          <Tab sx={{ textTransform: 'none', height: 50, fontFamily: 'Poppins', fontSize: '13px', fontWeight: 600, lineHeight: '19.5px', textAlign: 'center', background: 'rgba(255, 255, 255, 0.35)', color: 'rgba(63, 66, 84, 0.64)', marginRight: '5px' }} {...a11yProps(unfraudedTabIndex)} label="Unfrauded" />
        )}
      </Tabs>

      <CustomTabPanel value={value} index={0}><TiersSection listOfParties={infoSinistre?.involvedParties} /></CustomTabPanel>
      <CustomTabPanel value={value} index={1}><PoliceSection listOfPolice={infoSinistre?.involvedPolicies} /></CustomTabPanel>
      <CustomTabPanel value={value} index={2}><VehiculeSection listOfCars={infoSinistre?.involvedCars} /></CustomTabPanel>
      <CustomTabPanel value={value} index={3}><ImagesTab claimNumber={infoSinistre?.claimNumber} claimStatus={infoSinistre?.status} enableDelete /></CustomTabPanel>
      <CustomTabPanel value={value} index={4}>
        <DocSection
          claimNumber={infoSinistre?.claimNumber}
          claimStatus={infoSinistre?.status}
          reportingNumber={infoSinistre?.additionalData?.reportingNumber}
          policyNumber={infoSinistre?.involvedPolicies?.[0]?.policyUid}
          partyUid={infoSinistre?.involvedParties?.[0]?.partyUid}
          enableDelete
        />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={5}>
        <ExpertisesSection
          key={infoSinistre?.uid || infoSinistre?._id || infoSinistre?.claimNumber}
          claimNumber={infoSinistre?.claimNumber}
          relatedClaimUid={infoSinistre?.uid || infoSinistre?._id}
        />
      </CustomTabPanel>

      <CustomTabPanel value={value} index={6}><HistoriqueSection claimNumber={infoSinistre?.claimNumber} /></CustomTabPanel>
      {hasInvestigationAuth && (
        <CustomTabPanel value={value} index={7}><Investigations claimNumber={infoSinistre?.claimNumber} /></CustomTabPanel>
      )}
      {hasUnfraudedAuth && (
        <CustomTabPanel value={value} index={unfraudedTabIndex}>
          <UnfraudedSection
            claimNumber={infoSinistre?.claimNumber}
            infoSinistre={infoSinistre}
            clientUid={policyHolderUid}
            carUids={insuredCarUids}
          />
        </CustomTabPanel>
      )}
    </div>
  );
};
